import { Fragment, ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import ButtonOutlinedCentered from "../../../components/ButtonOutlined";
import GCBackButton from "../../../components/GreenCity/GCBackButton";
import ImageViewer from "../../../components/GreenCity/ImageViewer";
import LoadingModal from "../../../components/LoadingModal";
import MessageModal from "../../../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../../../components/ErrorMessageModal";
import TwoButtonModal from "../../../components/TwoButtonModal";
import { Avatar, Box, MenuItem, Select, Typography } from "@mui/material";
import ReimbursementTable from "../../../components/ReimbursementTable";
import style from "./styles";
import appServices from "../../../services/app-services/appServices";

const ACCEPT_MESSAGE = "立て替え精算の申請金額を送金します。";

function ShowGroupReimbursements(): ReactElement {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const years = Array.from({ length: 50 }, (_, i) => 2024 + i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const navigate = useNavigate();
  const groupInfo = useSelector((state: any) => state.gcGroup);
  const { user } = useSelector((state: any) => state.userAuth);
  const [reimbursements, setReimbursements] = useState<any[]>([]);
  const [images, setImages] = useState<any>([]);
  const [message, setMessage] = useState<string>("");
  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false)
  const [openErrorMessageModal, setOpenErrorMessageModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth.toString());
  const [showApprovalConfirmation, setShowApprovalConfirmation] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [confirmationStatusList, setConfirmationStatusList] = useState<{id: number, isProcessing: boolean}[]>();
  // Fetch reimbursements
  useEffect(() => {
    if (user?.id) {
      getPaymentReimbursements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPaymentReimbursements = async () => {
    try {
      setLoading(true);
      const res = await appServices.getPaymentGroupReimbursements(groupInfo.id, selectedYear, selectedMonth);
      setConfirmationStatusList(res.reimbursements?.map((item: any) => {
        return { id: item.id, isProcessing: false}
      }))
      setReimbursements(res.reimbursements || []); // Ensure fallback to an empty array
    } catch (error) {
      console.error("Error fetching reimbursements:", error);
    } finally {
      setLoading(false);
    }
  };

  const approveReinbursement = async (id: any) => {
    setConfirmationStatusList(confirmationStatusList?.map((item: any) => {
      return item.id === id ? { id: item.id, isProcessing: true} : item
    }))
    try {
      setShowApprovalConfirmation(false);
      setLoading(true);
      let msg = T("reimbursement_succeed_in_approve");
      const res = await appServices.approveReimbursement(id);
      if (res.success) {
        setMessage(msg);
        setOpenMessageModal(true)
        setTimeout(() => {
          setMessage("");
          navigate(-1);
        }, 3099);
      } else {
        setMessage(res?.message);
        setOpenErrorMessageModal(true)
        setConfirmationStatusList(confirmationStatusList?.map((item: any) => {
          return item.id === id ? { id: item.id, isProcessing: false} : item
        }))
      }
      return true
    } catch (error: any) {
      const errorResponse = error.response.data.message
      setMessage(
        typeof errorResponse === "object" ?
        errorResponse.join("\n") :
        errorResponse
      );
      setOpenErrorMessageModal(true)
      setConfirmationStatusList(confirmationStatusList?.map((item: any) => {
        return item.id === id ? { id: item.id, isProcessing: false} : item
      }))
      return false
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ paddingTop: 100, margin: 4 }}>
      <GCBackButton onClick={() => navigate(-1)} />
      <LoadingModal visible={loading} />
      <MessageModal
        onContinue={() => {}}
        message={message}
        open={openMessageModal}
        onClose={() => setOpenMessageModal(false)}
      />
      <ErrorMessageModal
        messages={[message]}
        open={openErrorMessageModal}
        onClose={() => setOpenErrorMessageModal(false)}
        showButton
        alignLeft={message.indexOf("\n") !== -1}
      />

      <div style={{ margin: 14 }}>
        <Box sx={style.rowMargin}>
          <Avatar
            src={groupInfo?.groupImage instanceof File ? URL.createObjectURL(groupInfo.groupImage) : ''}
            sx={{ ...style.avatar, width: "30px" }}
          />
          <Typography>{groupInfo?.groupName}</Typography>
        </Box>
        <Typography sx={style.titleSeprator}>{T("reimbursement_history")}</Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            marginTop: "10px",
          }}
        >
          <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} displayEmpty sx={{ width: "33%", backgroundColor: "white", height: "40px" }}>
            <MenuItem value="" disabled>
              {selectedYear} {T("year")}
            </MenuItem>
            {years.map((yr) => (
              <MenuItem key={yr} value={yr}>
                {yr}
              </MenuItem>
            ))}
          </Select>
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} displayEmpty sx={{ width: "30%", backgroundColor: "white", height: "40px" }}>
            <MenuItem value="" disabled>
              {selectedMonth} {T("month")}
            </MenuItem>
            {months.map((mn) => (
              <MenuItem key={mn} value={mn}>
                {mn}
              </MenuItem>
            ))}
          </Select>

          <ButtonOutlinedCentered onClick={getPaymentReimbursements} title={T("display")} />
        </Box>
        {reimbursements.map((reimbursement: any, index: number) => (
          <Fragment key={index}>
            <ReimbursementTable reimbursement={reimbursement} />
            {reimbursement.status === "pending" && (
              <>
                <ButtonOutlinedCentered
                  disabled={confirmationStatusList?.find((element: any) => element.id === reimbursement.id && reimbursement.isProcessing) != undefined}
                  onClick={() => {
                    setShowApprovalConfirmation(true);
                    setSelectedId(reimbursement.id);
                  }}
                  title={T("reimbursement_settle")}
                />
                <br />
                <ButtonOutlinedCentered
                  onClick={() => navigate("/rejectreimbursement", { state: { item: reimbursements.find((element: any) => element.id === reimbursement.id) } })}
                  title={T("reimbursement_reject")}
                />
              </>
            )}
            <hr />
          </Fragment>
        ))}
      </div>
      <ImageViewer images={images} isModalOpen={images.length > 0} onClose={() => setImages([])} />
      <TwoButtonModal
        open={showApprovalConfirmation}
        onClose={() => setShowApprovalConfirmation(false)}
        message={ACCEPT_MESSAGE}
        onContinue={() => approveReinbursement(selectedId)}
        noText={T("no")}
      />
    </div>
  );
}

export default ShowGroupReimbursements;
